import { injectable } from 'inversify'

/**
 * Wraps window.open/location (invariant 8: all browser APIs behind a gateway).
 */
@injectable()
export class WindowGateway {
  open(url: string): void {
    window.open(url, '_blank')
  }

  reload(): void {
    window.location.reload()
  }

  /**
   * Rewrites the address bar in place, no history entry — used to strip a
   * one-time token out of the URL after it has been consumed, so it never
   * sits in history or a bookmark.
   */
  replacePath(path: string): void {
    window.history.replaceState(null, '', path)
  }

  /**
   * The address bar as the app currently sees it. Router reads this once, to
   * decide whether a page load started at a bare '/' and is therefore eligible
   * to resume the last route.
   */
  location(): { pathname: string; search: string } {
    return { pathname: window.location.pathname, search: window.location.search }
  }

  /** Scheme + host (+ port) this app is served from — used to turn a backend-given relative path (the calendar feed's `feedPath`) into a full, copyable URL. Never the backend's own origin; that is `Config.apiUrl`, a build-time value, not window state. */
  origin(): string {
    return window.location.origin
  }

  /**
   * Asks the browser to confirm before the document goes away — reload, tab
   * close, a link to another origin. Returns a disposer.
   *
   * `shouldWarn` is consulted at unload time rather than the listener being
   * added and removed as state changes, so there is no window where the flag
   * and the listener disagree.
   *
   * No message is passed: every current browser ignores a custom string and
   * shows its own wording. Measured — the dialog arrives with an empty message
   * whatever is returned.
   *
   * This does NOT fire for in-app navigation or for back/forward: those are
   * same-document navigations and the document is not going anywhere. Also
   * measured, because the difference is the entire reason this exists rather
   * than a general-purpose guard.
   */
  onBeforeUnload(shouldWarn: () => boolean): () => void {
    const handler = (event: BeforeUnloadEvent) => {
      if (!shouldWarn()) return
      event.preventDefault()
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }

  /**
   * Saves `content` to disk as `filename` — the whole of a client-built
   * export download (settings-page.presenter.ts): no `<a download>` markup
   * anywhere else, so this is the one place that creates and clicks it.
   * `content` is already a string (JSON.stringify'd or CSV text) rather than
   * a Blob, so a caller never has to know the DOM APIs this hides behind.
   */
  download(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
  }

  /** Wraps the Clipboard API (invariant 8) — used for "copy" buttons (the calendar-feed URL in Settings). Resolves false rather than throwing when the browser refuses (no permission, insecure context), so a caller can show "couldn't copy" instead of an unhandled rejection. */
  async writeText(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      return false
    }
  }

  viewportSize(): { width: number; height: number } {
    return { width: window.innerWidth, height: window.innerHeight }
  }

  onViewportResize(handler: (size: { width: number; height: number }) => void): () => void {
    const onResize = () => handler(this.viewportSize())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }
}
